import { useCallback, useEffect, useState } from 'react';
import { ROLE_LABELS, type InvitationPreview } from '@icetus/contracts';
import { Button } from '@icetus/ui';
import { Brand } from '../components/brand';
import { acceptInvitation, ApiError, previewInvitation, signupFromInvitation } from '../api';
import type { MeResponse } from '../api';

// 指向 icetus.com：官网主域已收口到 .com，.net 只服务客户站点（在 CF 后面）。
// 邀请邮件的收件人本来就还没账号，落地页上这几条链接是他唯一能了解产品的地方。
const SITE_HOME_URL = 'https://icetus.com';
const TERMS_URL = 'https://icetus.com/terms';
const PRIVACY_URL = 'https://icetus.com/privacy';

/**
 * 邀请链接的落地页。
 *
 * 这个页面要处理一件别人不太会注意的事：**收件人此刻通常还没登录，
 * 而且很可能还没有账号**。所以它得按三种身份各走一遍：
 *
 *   1) 没登录 → 先看得到"谁邀请你、什么角色"。
 *      邮箱还没账号的，**就在本页设密码建号**（`POST /v1/invitations/<token>/signup`）——
 *      这条路只建账号、不建站：他加入的是一家已经有站的公司，再给他开一个
 *      空站只会让他登录后分不清哪个是自己的。
 *      已有账号的，摆登录框（就是控制台那个，不另写一套 —— 两套登录表单
 *      迟早有一处忘了加限流或错误文案）
 *   2) 登录了但不是被邀请的邮箱 → 明确告诉他是哪个邮箱，而不是等点下去 409
 *   3) 都对 → 一个按钮接受，接受完刷新租户名册再进控制台
 *
 * 令牌只放在 URL 里，不再落到 localStorage：落地页刷新一次就该还能用，
 * 而存在浏览器里的令牌会跟着这台机器走，换个同事打开就变成他的了。
 */

interface InviteGateProps {
  token: string;
  me: MeResponse | null;
  /**
   * 未登录时渲染的登录表单。传进去的 hint 会显示在登录卡片里 ——
   * 复用控制台那一个，而不是在这个页面再写一套登录框：两套表单迟早
   * 有一处忘了限流、忘了错误文案，而登录恰恰是最不能分叉的地方。
   */
  renderLogin: (hint: React.ReactNode) => React.ReactNode;
  /** 换账号：断开服务端会话，否则再登录会沿用旧身份 */
  onSignOut: () => Promise<void>;
  /** 接受成功后重新拉一次 /v1/auth/me，让新租户出现在下拉里 */
  onJoined: () => Promise<void>;
}

type Phase =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; preview: InvitationPreview }
  | { kind: 'joined'; tenantName: string; role: string };

export function InviteGate({
  token,
  me,
  renderLogin,
  onSignOut,
  onJoined,
}: InviteGateProps) {
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * 邀请链接上建账号的状态。
   *
   * `signupSent` 存的是邮箱而不是一个布尔：成功卡片要把地址念出来
   * （"已发到 a@b.com"），而此刻 `preview.hasAccount` 还是 false ——
   * 那是页面打开时取的旧值，服务端刚建的账号它并不知道。
   */
  const [signupPassword, setSignupPassword] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [signupBusy, setSignupBusy] = useState(false);
  const [signupError, setSignupError] = useState<string | null>(null);
  const [signupSent, setSignupSent] = useState<string | null>(null);
  /** 已经说过"我注册过"的人，不要再给他看注册表单 */
  const [preferLogin, setPreferLogin] = useState(false);

  const load = useCallback(async () => {
    try {
      setPhase({ kind: 'ready', preview: await previewInvitation(token) });
    } catch (err) {
      setPhase({
        kind: 'error',
        message:
          err instanceof ApiError && err.status === 404
            ? '这个邀请链接无效或已过期。请让管理员在「成员」页重新生成一条。'
            : err instanceof ApiError
              ? err.message
              : '邀请链接读取失败',
      });
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * 在本页建账号。
   *
   * 建完**不自动登录**：账号要先去邮件里验证（接受邀请的接口要求邮箱已验证），
   * 现在给他一个会话，他点接受照样被拦，反而更困惑。
   */
  const submitSignup = async (): Promise<void> => {
    if (signupBusy || !signupPassword || !accepted) return;
    setSignupBusy(true);
    setSignupError(null);
    try {
      const result = await signupFromInvitation(token, signupPassword);
      setSignupSent(result.email);
      setSignupPassword('');
    } catch (err) {
      setSignupError(err instanceof ApiError ? err.message : '创建账号失败');
    } finally {
      setSignupBusy(false);
    }
  };

  if (phase.kind === 'loading') {
    return <div className="boot">正在读取邀请…</div>;
  }

  if (phase.kind === 'error') {
    return (
      <div className="login">
        <div className="login__card">
          <h2 className="login__title">邀请不可用</h2>
          <p className="muted">{phase.message}</p>
          <Button variant="primary" onClick={() => window.location.replace('/')}>
            打开控制台
          </Button>
        </div>
      </div>
    );
  }

  if (phase.kind === 'joined') {
    return (
      <div className="login">
        <div className="login__card">
          <h2 className="login__title">已加入 {phase.tenantName}</h2>
          <p className="muted">
            你在 {phase.tenantName} 的身份是「{phase.role}」，现在可以进入控制台了。
          </p>
          <Button
            variant="primary"
            onClick={() => {
              // 用 replace 而不是 push：接受完再点后退不该回到这个已用过的令牌
              window.location.replace('/');
            }}
          >
            进入控制台
          </Button>
        </div>
      </div>
    );
  }

  // 走到这里必然是 ready
  const preview = phase.preview;

  // 状态不对时不做任何动作：已用过/已撤销/已过期的链接再点一次都不该"成功"
  if (preview.status !== 'pending') {
    const wording: Record<InvitationPreview['status'], string> = {
      pending: '',
      accepted: '这个邀请已经被接受过了，直接用那个账号登录即可。',
      expired: '这个邀请已过期。请让管理员在「成员」页重新生成一条。',
      revoked: '这个邀请已被撤销。请让管理员重新邀请。',
    };
    return (
      <div className="login">
        <div className="login__card">
          <h2 className="login__title">{preview.tenantName}</h2>
          <p className="muted">{wording[preview.status]}</p>
          <Button variant="primary" onClick={() => window.location.replace('/')}>
            打开控制台
          </Button>
        </div>
      </div>
    );
  }

  const roleLabel = ROLE_LABELS[preview.role];

  // ① 没登录。这里分两件事：
  //    a) 邮箱还没账号 → 直接在本页建号（不建站），再去验证、登录
  //    b) 已有账号 / 刚建完号 / 他自己说"注册过" → 摆登录框
  //    先讲清"谁邀请你、必须用哪个邮箱"，否则他点进来只看到普通登录页，会以为链接坏了
  if (!me) {
    if (!preview.hasAccount && !preferLogin && !signupSent) {
      return (
        <div className="login">
          <div className="login__card">
            <Brand />
            <h2 className="login__title">加入 {preview.tenantName}</h2>
            <p className="muted">
              {preview.tenantName} 邀请你以「{roleLabel}」的身份加入。为{' '}
              <strong>{preview.email}</strong> 设一个密码就能加入 ——{' '}
              <strong>不会给你新建站点</strong>，这个账号只属于这家公司。
            </p>
            <p className="notice notice--warn">
              只能用 <strong>{preview.email}</strong> 建号。链接可以被转发，但只有这个邮箱能加入。
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void submitSignup();
              }}
            >
              <input
                className="field"
                type="password"
                autoFocus
                autoComplete="new-password"
                placeholder="密码（至少 12 位）"
                value={signupPassword}
                onChange={(e) => setSignupPassword(e.target.value)}
              />
              <label className="login__consent">
                <input
                  type="checkbox"
                  checked={accepted}
                  onChange={(e) => setAccepted(e.target.checked)}
                />
                <span>
                  我已阅读并同意
                  <a
                    href={TERMS_URL}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    《服务条款》
                  </a>
                  与
                  <a
                    href={PRIVACY_URL}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    《隐私政策》
                  </a>
                </span>
              </label>
              <Button
                variant="primary"
                type="submit"
                disabled={signupBusy || !signupPassword || !accepted}
              >
                {signupBusy ? '创建中…' : '创建账号'}
              </Button>
            </form>
            {signupError ? <p className="error">{signupError}</p> : null}
            <button type="button" className="login__switch" onClick={() => setPreferLogin(true)}>
              这个邮箱已经注册过？直接登录
            </button>
          </div>
          <a className="login__home" href={SITE_HOME_URL}>
            返回 iCetus 官网
          </a>
        </div>
      );
    }

    return renderLogin(
      <>
        <h2 className="login__title">加入 {preview.tenantName}</h2>
        {signupSent ? (
          <>
            <p className="notice notice--ok">
              账号已建好，验证邮件已发到 <strong>{signupSent}</strong>。
            </p>
            <p className="muted">
              点开邮件里的链接会回到这一页，再用刚设置的密码登录就能加入
              {preview.tenantName}。没收到的话先看垃圾箱。
            </p>
          </>
        ) : (
          <>
            <p className="muted">
              {preview.tenantName} 邀请你以「{roleLabel}」的身份加入。
              {preview.hasAccount
                ? '这个邮箱已经有账号了，用密码登录即可。'
                : '用这个邮箱登录即可；还没有账号的话，回到邀请链接上设一个密码就能加入。'}
            </p>
            <p className="notice notice--warn">
              必须用 <strong>{preview.email}</strong> 登录。链接可以被转发，但只有这个邮箱能接受。
            </p>
          </>
        )}
      </>,
    );
  }

  // ② 登录了，但不是被邀请的那个邮箱。提前讲清楚，别让他点了才收到 409
  if (me.user && me.user.email.toLowerCase() !== preview.email.toLowerCase()) {
    return (
      <div className="login">
        <div className="login__card">
          <h2 className="login__title">这个邀请不是发给你的</h2>
          <p className="muted">
            邀请发给 <strong>{preview.email}</strong>，而你当前登录的是{' '}
            <strong>{me.user.email}</strong>。
          </p>
          <p className="muted">
            先退出当前账号，再用上面那个邮箱登录，然后重新打开这条链接。
            那个邮箱如果还没有账号，重新打开链接时可以直接设密码建号 —— 只建账号，
            不会给他新建站点。
          </p>
          <Button
            variant="primary"
            onClick={async () => {
              // 只清本地状态不够，服务端会话也得断，否则再登录会沿用旧身份
              await onSignOut().catch(() => undefined);
              window.location.replace('/');
            }}
          >
            去换账号
          </Button>
        </div>
      </div>
    );
  }

  // ③ 邮箱对上了。未验证邮箱也能接受 —— 服务端要求 @RequireVerified，
  //    这里提前提示，免得他点了之后收到一句"请先验证邮箱"
  const unverified = me.user ? !me.user.emailVerified : false;

  return (
    <div className="login">
      <div className="login__card">
        <h2 className="login__title">加入 {preview.tenantName}</h2>
        <p className="muted">
          以「{roleLabel}」的身份加入 {preview.tenantName}，用 {preview.email} 登录。
        </p>
        {unverified ? (
          <p className="notice notice--warn">
            你的邮箱还没验证。先到控制台顶部点「重发验证邮件」，点开邮件里的链接后再回来接受。
          </p>
        ) : null}
        {error ? <p className="error">{error}</p> : null}
        <Button
          variant="primary"
          disabled={busy || unverified}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              const result = await acceptInvitation(token);
              await onJoined();
              window.history.replaceState({}, '', '/');
              setPhase({
                kind: 'joined',
                tenantName: result.tenantName,
                role: ROLE_LABELS[result.role],
              });
            } catch (err) {
              setError(err instanceof ApiError ? err.message : '接受失败');
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? '加入中…' : `接受邀请，加入 ${preview.tenantName}`}
        </Button>
      </div>
    </div>
  );
}
